// Модель податкової накладної: одна для майстра й швидкого редактора, з неї — перевірки й XML.
// Документ (doc) зберігається як є в чернетці (pn_drafts.doc); значення полів — рядки, як їх ввели.

// Сценарії «Що ви хочете оформити?». ready: false — видно в переліку з позначкою «Скоро»
export const KINDS = {
  sale: { title: "Продаж товарів або послуг", hint: "Покупець — платник ПДВ, у тому числі передоплата: дата ПН — перша подія (відвантаження чи оплата).", ready: true },
  nonpayer: { title: "Продаж покупцю без ПДВ", hint: "Фізична особа або ФОП-неплатник. ПН реєструєте ви, покупцю вона не надається.", ready: true },
  // Об'єднано з «Продажем» (2026-10-11): документ той самий, різнилась лише назва поля дати. Нових не створюємо,
  // наявні чернетки відкриваються як були
  prepay: { title: "Отримали передоплату", hint: "Гроші прийшли раніше за товар чи послугу — ПН складається на дату оплати.", ready: false, merged: "sale" },
  correction: { title: "Виправити накладну (РК)", hint: "Зміна ціни, кількості, повернення, помилка в реквізитах.", ready: false },
  other: { title: "Інша операція або не знаю, що вибрати", hint: "Експорт, звільнені операції, самопостачання, ліквідація тощо.", ready: false },
};

// ПН неплатнику (Порядок №1307, п.8, 10–14): не надається покупцю, тип причини 02,
// умовний ІПН 100000000000, назва покупця «Неплатник»
export const NONPAYER = { ipn: "100000000000", name: "Неплатник", reason: "02" };
// Та сама ПН неплатнику, складена за щоденними підсумками операцій (продажі за чеками РРО/ПРРО за день): тип причини 11
// (doc.reason = "11", doc.daily — за який день і з яких кас). Окремий від 02 сценарій — не переписує наявні документи
export const DAILY_REASON = "11";
export const nonpayerReason = (doc) => (doc?.kind === "nonpayer" && doc.reason === DAILY_REASON ? DAILY_REASON : NONPAYER.reason);

// Одиниці виміру: умовне позначення → код КСПОВО. Пари взято з реальних зареєстрованих ПН наших
// компаній як приклади сумісності; перед релізом звірити з класифікатором ДК 011-96 (КСПОВО).
// Для послуг код одиниці не заповнюється (ЄРПН відхиляє)
export const UNITS = [
  ["шт", "2009", "штука"], ["пач", "2112", "пачка"], ["пляш", "2061", "пляшка"], ["компл", "2938", "комплект"],
  ["кг", "0301", "кілограм"], ["т", "0306", "тонна"], ["м", "0101", "метр"], ["пог.м", "2702", "погонний метр"],
  ["м2", "0123", "квадратний метр"], ["м3", "0134", "кубічний метр"], ["л", "0138", "літр"], ["мл", "0144", "мілілітр"],
  ["пак", "2110", "пакет"], ["паков", "2102", "пакування"], ["рул", "2116", "рулон"], ["міш", "2060", "мішок"],
  ["набір", "2123", "набір"], ["пар", "1617", "пара"], ["банк", "2039", "банка"], ["каністр", "2048", "каністра"],
  ["тис.шт", "2013", "тисяча штук"], ["доб", "0174", "доба"], ["грн", "2454", "гривня"],
];
export const unitCode = (unit) => UNITS.find(([u]) => u === String(unit || "").trim())?.[1] || "";
export const SERVICE_UNIT = "послуга";

// Ставка нового рядка — стандартна 20% (рішення користувача 2026-10-11): її видно в полі й можна змінити.
// Ставка з таблиці чи з попереднього продажу того самого товару має пріоритет
export const DEFAULT_RATE = "20";

let seq = 0;
export const newRow = (over = {}) => ({
  id: `r${Date.now().toString(36)}${(seq++).toString(36)}`,
  name: "", type: "goods", uktzed: "", dkpp: "", imported: false, ownAgro: false,
  unit: "шт", qty: "1", price: "", sum: "", amountBy: "price", rate: DEFAULT_RATE, benefit: "", source: null,
  ...over,
});

export function newDoc(kind = "sale", seller = null) {
  return {
    kind,
    firstEvent: kind === "prepay" ? "payment" : null, // shipment | payment
    date: "",           // YYYY-MM-DD — дата першої події = дата ПН
    number: "",         // порядковий номер ПН (HNUM) — не службовий номер документа ДПС (C_DOC_CNT)
    seller,             // { name, ipn, tin, ks, director: { name, rnokpp }, reg, raj, sti, source }
    buyer: kind === "nonpayer" ? { name: NONPAYER.name, ipn: NONPAYER.ipn, tin: "", source: "nonpayer" } : { name: "", ipn: "", tin: "", source: null },
    priceMode: "net",   // net — ціни без ПДВ, gross — з ПДВ
    rows: [newRow()],
  };
}

// Код ознаки джерела податкового номера (HKB/HKS): 1 — ЄДРПОУ, 2 — ДРФО (РНОКПП)
export const tinSource = (tin) => (/^\d{8}$/.test(tin) ? "1" : /^\d{10}$/.test(tin) ? "2" : "");

// Строк реєстрації (п.89 підрозд. 2 розд. XX ПКУ, воєнний стан + 6 міс.): складені 1–15 числа — до 5-го
// числа наступного місяця, з 16-го — до 18-го. Джерело вторинне — звірити з чинним текстом ПКУ.
export function registrationDeadline(isoDate) {
  const m = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const ny = mo === 12 ? y + 1 : y, nm = mo === 12 ? 1 : mo + 1;
  return `${ny}-${String(nm).padStart(2, "0")}-${d <= 15 ? "05" : "18"}`;
}

export const isoToDdmmyyyy = (iso) => (iso ? iso.slice(8, 10) + iso.slice(5, 7) + iso.slice(0, 4) : "");
